import classnames from "classnames";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { texts } from "modules/common/texts";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./ContextMenu.module.scss";

/** Отступ от края окна */
const VIEWPORT_GAP = 8;

export interface ContextMenuAction {
    key: string;
    label: string;
    icon: string;
    /** Красный пункт: необратимое действие */
    danger?: boolean;
    /** Зелёный пункт: действие завершает работу */
    success?: boolean;
    disabled?: boolean;
    hint?: string;
    /** Разделитель с необязательным заголовком группы */
    separator?: boolean;
    onClick: () => void;
}

interface ContextMenuProps {
    x: number;
    y: number;
    actions: ContextMenuAction[];
    onClose: () => void;
}

/**
 * Контекстное меню по правой кнопке: план работ и доска заданий показывают им свои действия.
 * Данные меню не хранит — получает готовый список действий, состоянием владеет страница.
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, actions, onClose }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<React.CSSProperties>({ left: x, top: y, visibility: "hidden" });

    // у нижних и правых строк меню разворачивается в сторону экрана
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!element) {
            return;
        }

        const { width, height } = element.getBoundingClientRect();

        const top =
            y + height + VIEWPORT_GAP <= window.innerHeight ? y : Math.max(VIEWPORT_GAP, y - height);
        const left = x + width + VIEWPORT_GAP <= window.innerWidth ? x : Math.max(VIEWPORT_GAP, x - width);

        setPosition({ left, top, visibility: "visible" });
    }, [x, y]);

    useEffect(() => {
        // Закрываем только по нажатию ВНЕ меню: слушатель висит на document в фазе
        // перехвата, поэтому без этой проверки он срабатывал бы раньше click по пункту
        // и действие не успевало выполниться.
        const onPointerDown = (event: PointerEvent) => {
            if (!rootRef.current || !rootRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
            }
        };

        const close = () => onClose();

        document.addEventListener("pointerdown", onPointerDown, true);
        document.addEventListener("keydown", onKeyDown, true);
        window.addEventListener("resize", close);

        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
            document.removeEventListener("keydown", onKeyDown, true);
            window.removeEventListener("resize", close);
        };
    }, [onClose]);

    return ReactDOM.createPortal(
        <div className={styles.menu} style={position} ref={rootRef}>
            {actions.map((action) =>
                action.separator ? (
                    <div key={action.key} className={styles.separator}>
                        {action.label}
                    </div>
                ) : (
                <div
                    key={action.key}
                    className={classnames(styles.item, {
                        [styles.danger]: action.danger,
                        [styles.success]: action.success,
                        [styles.disabled]: action.disabled,
                    })}
                    onClick={() => {
                        if (!action.disabled) {
                            action.onClick();
                            onClose();
                        }
                    }}
                >
                    <GeneralIcon type={action.icon as any} />
                    <span className={styles.label}>{action.label}</span>
                    {action.hint && <span className={styles.hint}>{action.hint}</span>}
                </div>
                )
            )}
        </div>,
        document.body
    );
};

export const contextMenuTexts = {
    copy: texts.copy,
    paste: "Вставить",
    clear: "Очистить",
};
